// client/src/components/leads/b2c/CustomersPage.js
// FINAL VERSION - Responsive buttons, compact design

import React, { useState, useEffect } from 'react';
import CustomerList from './CustomerList';
import AddCustomerModal from './AddCustomerModal';
import UploadCustomersModal from './UploadCustomersModal';
import CustomerDetail from './CustomerDetail';
import SendEmailModal from './SendEmailModal';
import { customerAPI } from '../../../services/customerAPI';

export default function CustomersPage({ businessData, customers, setCustomers }) {
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterSegment, setFilterSegment] = useState('all');
  const [segmentStats, setSegmentStats] = useState(null);
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [accessError, setAccessError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadCustomers();
    loadSegmentStats();
  }, []);

  useEffect(() => {
    if (filterSegment !== 'all') {
      loadCustomersBySegment(filterSegment);
    } else {
      loadCustomers();
    }
  }, [filterSegment]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setAccessBlocked(false);
      const response = await customerAPI.getAll();
      if (response.success) {
        setCustomers(response.data || []);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      // Check if error is 403 (subscription tier block)
      if (error.response?.status === 403) {
        setAccessBlocked(true);
        setAccessError(error.response?.data);
      }
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomersBySegment = async (segment) => {
    try {
      setLoading(true);
      const response = await customerAPI.getSegment(segment);
      if (response.success) {
        setCustomers(response.data || []);
      }
    } catch (error) {
      console.error('Error loading segment:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSegmentStats = async () => {
    try {
      const response = await customerAPI.getSegmentStats();
      if (response.success) {
        setSegmentStats(response.data);
      }
    } catch (error) {
      console.error('Error loading segment stats:', error);
    }
  };

  const handleAddCustomer = async (customerData) => {
    try {
      const response = await customerAPI.create(customerData);
      if (response.success) {
        await loadCustomers();
        await loadSegmentStats();
        setShowAddModal(false);
      } else {
        throw new Error(response.error || 'Failed to add customer');
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      throw error;
    }
  };

  const handleBulkUpload = async (customersData) => {
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < customersData.length; i++) {
        try {
          const response = await customerAPI.create(customersData[i]);
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`Row ${i + 1}: ${response.error}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 1}: ${error.message}`);
        }
      }

      await loadCustomers();
      await loadSegmentStats();

      if (errorCount === 0) {
        alert(`✅ Successfully imported ${successCount} customers!`);
      } else {
        alert(`Import complete:\n✅ ${successCount} successful\n❌ ${errorCount} failed\n\nErrors:\n${errors.slice(0, 5).join('\n')}`);
      }

      setShowUploadModal(false);
    } catch (error) {
      console.error('Bulk upload error:', error);
      throw error;
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleCloseDetail = () => {
    setSelectedCustomer(null);
  };

  const handleUpdateCustomer = async () => {
    await loadCustomers();
    await loadSegmentStats();
    if (selectedCustomer) {
      try {
        const response = await customerAPI.getById(selectedCustomer.id);
        if (response.success) {
          setSelectedCustomer(response.data);
        }
      } catch (error) {
        console.error('Error refreshing customer:', error);
      }
    }
  };

  const handleDeleteCustomer = async () => {
    setSelectedCustomer(null);
    await loadCustomers();
    await loadSegmentStats();
  };

  const handleSendBulkEmail = () => {
    setShowEmailModal(true);
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Phone', 'Email', 'Source', 'Date Added'];
    const csvRows = [headers.join(',')];
    
    filteredCustomers.forEach(customer => {
      const row = [
        `"${customer.name || ''}"`,
        `"${customer.phone || ''}"`,
        `"${customer.email || ''}"`,
        `"${customer.source || ''}"`,
        `"${new Date(customer.createdAt || customer.created_at).toLocaleDateString()}"`
      ];
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredCustomers = (customers || []).filter(customer => {
    const matchesSearch = !searchTerm || 
                          customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = filterSource === 'all' || customer.source === filterSource;
    return matchesSearch && matchesSource;
  });

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterSource, filterSegment, itemsPerPage]);

  const sources = ['all', ...new Set(customers.map(c => c.source).filter(Boolean))];

  const getSegmentCount = (segment) => {
    if (!segmentStats) return 0;
    
    const counts = {
      all: segmentStats.total || 0,
      vip: segmentStats.vip || 0,
      frequent: segmentStats.frequent || 0,
      dormant: segmentStats.dormant || 0,
      'at-risk': segmentStats.atRisk || 0,
      new: segmentStats.new || 0,
      regular: segmentStats.regular || 0
    };
    
    return counts[segment] || 0;
  };

  const getSegmentIcon = (segment) => {
    const icons = {
      all: 'fas fa-users',
      vip: 'fas fa-star',
      frequent: 'fas fa-sync',
      dormant: 'fas fa-exclamation-triangle',
      'at-risk': 'fas fa-heart-broken',
      new: 'fas fa-user-plus',
      regular: 'fas fa-user'
    };
    return icons[segment] || 'fas fa-users';
  };

  const getSegmentColor = (segment) => {
    const colors = {
      all: 'bg-gray-100 text-gray-700 border-gray-300',
      vip: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      frequent: 'bg-blue-100 text-blue-700 border-blue-300',
      dormant: 'bg-orange-100 text-orange-700 border-orange-300',
      'at-risk': 'bg-red-100 text-red-700 border-red-300',
      new: 'bg-green-100 text-green-700 border-green-300',
      regular: 'bg-purple-100 text-purple-700 border-purple-300'
    };
    return colors[segment] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  // Show upgrade prompt if access is blocked due to subscription tier
  if (accessBlocked) {
    return (
      <div className="p-6">
        <div className="max-w-3xl mx-auto mt-12">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-600/20">
              <i className="fas fa-lock text-white text-3xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">CRM Access Requires Upgrade</h2>
            <p className="text-gray-700 mb-6 max-w-xl mx-auto">
              {accessError?.message || 'This feature requires Assisted Growth or Fully Managed VIP subscription'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
              <div className="bg-white rounded-lg p-6 border border-blue-200">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-rocket text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Assisted Growth</h3>
                <p className="text-3xl font-bold text-blue-600 mb-2">£129<span className="text-sm text-gray-500">/mo</span></p>
                <ul className="text-sm text-gray-600 space-y-2 mb-4 text-left">
                  <li><i className="fas fa-check text-green-600 mr-2"></i>Up to 500 customers</li>
                  <li><i className="fas fa-check text-green-600 mr-2"></i>8 AI auto-posts/month</li>
                  <li><i className="fas fa-check text-green-600 mr-2"></i>Monthly strategy call</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-600">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-crown text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Fully Managed VIP</h3>
                <p className="text-3xl font-bold text-green-600 mb-2">£449<span className="text-sm text-gray-500">/mo</span></p>
                <ul className="text-sm text-gray-600 space-y-2 mb-4 text-left">
                  <li><i className="fas fa-check text-green-600 mr-2"></i>Unlimited customers</li>
                  <li><i className="fas fa-check text-green-600 mr-2"></i>20 posts/month + custom</li>
                  <li><i className="fas fa-check text-green-600 mr-2"></i>Full CRM management</li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/settings?tab=billing"
                className="px-8 py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 inline-flex items-center justify-center"
              >
                <i className="fas fa-arrow-up mr-2"></i>
                Upgrade Now
              </a>
              <a
                href="/dashboard"
                className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:border-gray-300 hover:shadow-sm transition-all inline-flex items-center justify-center"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Back to Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header with Responsive Buttons */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-gray-900">Customers</h2>
          <p className="text-xs text-gray-600 mt-0.5">Manage your customer database • {customers.length} total</p>
        </div>
        
        {/* Responsive Button Group */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-3 py-1.5 bg-white border border-green-600 text-green-600 text-xs font-medium rounded-lg hover:bg-green-50 transition-colors"
          >
            <i className="fas fa-file-upload mr-1.5"></i>
            Upload
          </button>
          
          <button
            onClick={handleExportCSV}
            disabled={filteredCustomers.length === 0}
            className="px-3 py-1.5 bg-white border border-green-600 text-green-600 text-xs font-medium rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fas fa-download mr-1.5"></i>
            Export
          </button>

          <button
            onClick={handleSendBulkEmail}
            disabled={filteredCustomers.length === 0}
            className="px-3 py-1.5 bg-white border border-green-600 text-green-600 text-xs font-medium rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fas fa-envelope mr-1.5"></i>
            Email
          </button>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <i className="fas fa-plus mr-1.5"></i>
            Add Customer
          </button>
        </div>
      </div>

      {/* Compact Segment Filters */}
      <div className="bg-white border border-gray-300 rounded-lg p-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {['all', 'vip', 'frequent', 'dormant', 'at-risk', 'new', 'regular'].map(segment => (
            <button
              key={segment}
              onClick={() => setFilterSegment(segment)}
              className={`flex-shrink-0 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                filterSegment === segment
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : `${getSegmentColor(segment)} hover:shadow-sm`
              }`}
            >
              <div className="flex items-center gap-2">
                <i className={getSegmentIcon(segment)}></i>
                <span className="capitalize">{segment === 'at-risk' ? 'At Risk' : segment}</span>
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-bold ${
                  filterSegment === segment
                    ? 'bg-white/20'
                    : 'bg-white/40'
                }`}>
                  {getSegmentCount(segment)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white border border-gray-300 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            >
              {sources.map(source => (
                <option key={source} value={source}>
                  {source === 'all' ? 'All Sources' : source.charAt(0).toUpperCase() + source.slice(1)}
                </option>
              ))}
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            >
              <option value={10}>Show 10</option>
              <option value={25}>Show 25</option>
              <option value={50}>Show 50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer List */}
      {filteredCustomers.length === 0 ? (
        <div className="bg-white border border-gray-300 rounded-lg p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-users text-gray-400 text-3xl"></i>
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-2">
            {searchTerm || filterSource !== 'all' || filterSegment !== 'all' ? 'No customers found' : 'No customers yet'}
          </h3>
          <p className="text-xs text-gray-600 mb-6">
            {searchTerm || filterSource !== 'all' || filterSegment !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Start by adding your first customer'}
          </p>
          {!searchTerm && filterSource === 'all' && filterSegment === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i className="fas fa-plus mr-2"></i>
              Add Your First Customer
            </button>
          )}
        </div>
      ) : (
        <>
          <CustomerList 
            customers={currentCustomers}
            onViewCustomer={handleViewCustomer}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white border border-gray-300 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-xs text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredCustomers.length)} of {filteredCustomers.length}
                </p>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Prev
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2 text-gray-500">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCustomer}
          businessData={businessData}
        />
      )}

      {showUploadModal && (
        <UploadCustomersModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleBulkUpload}
        />
      )}

      {showEmailModal && (
        <SendEmailModal
          segment={filterSegment}
          customers={filteredCustomers}
          onClose={() => setShowEmailModal(false)}
          onEmailSent={() => setShowEmailModal(false)}
        />
      )}

      {selectedCustomer && (
        <CustomerDetail
          customer={selectedCustomer}
          onClose={handleCloseDetail}
          onUpdate={handleUpdateCustomer}
          onDelete={handleDeleteCustomer}
        />
      )}
    </div>
  );
}